/**
 * Multi-tab notification election (R9, ENG-2827): when an agent has the CC
 * open in several tabs, every tab receives the same socket events — without
 * an election each one plays the escalation sound and fires a browser
 * notification. Exactly ONE tab (the leader) does; state updates stay
 * per-tab.
 *
 * Leadership is a localStorage heartbeat lease: the leader refreshes a
 * timestamped claim; any tab may take over once the claim goes stale (leader
 * closed/crashed — takeover within ~2 lease periods). localStorage is used
 * over BroadcastChannel because the lease must survive reads from tabs that
 * open AFTER the leader claimed.
 */

const LEASE_KEY = "swivl-cc-notify-leader";
const LEASE_TTL_MS = 8_000;
const HEARTBEAT_MS = 3_000;

const tabId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
let heartbeat: ReturnType<typeof setInterval> | null = null;

interface Lease {
  tabId: string;
  at: number;
}

function readLease(): Lease | null {
  try {
    const raw = localStorage.getItem(LEASE_KEY);
    return raw ? (JSON.parse(raw) as Lease) : null;
  } catch {
    return null;
  }
}

function claimIfFree(): void {
  try {
    const lease = readLease();
    const stale = !lease || Date.now() - lease.at > LEASE_TTL_MS;
    if (stale || lease.tabId === tabId) {
      localStorage.setItem(LEASE_KEY, JSON.stringify({ tabId, at: Date.now() } satisfies Lease));
    }
  } catch {
    /* storage unavailable → every tab notifies; better duplicated than silent */
  }
}

/** Start competing for leadership. Idempotent; call on CC page mount. */
export function startNotificationLeader(): void {
  if (heartbeat) return;
  claimIfFree();
  heartbeat = setInterval(claimIfFree, HEARTBEAT_MS);
}

/** Stop competing (CC page unmount) — releases the lease if we hold it. */
export function stopNotificationLeader(): void {
  if (heartbeat) {
    clearInterval(heartbeat);
    heartbeat = null;
  }
  try {
    const lease = readLease();
    if (lease?.tabId === tabId) localStorage.removeItem(LEASE_KEY);
  } catch {
    /* ignore */
  }
}

/** True when THIS tab should play sounds / fire browser notifications. */
export function isNotificationLeader(): boolean {
  const lease = readLease();
  if (!lease || Date.now() - lease.at > LEASE_TTL_MS) {
    // No live leader — claim on the spot so the notification isn't dropped.
    claimIfFree();
    return true;
  }
  return lease.tabId === tabId;
}
